import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bell, Search, MessageCircle, Award, Home, Trophy, ArrowLeft, X, Brain } from 'lucide-react';
import { getCurrentUser } from '../utils/auth';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import ProfileDrawer from './ProfileDrawer';
import { searchAPI, notificationsAPI } from '../services/api';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const searchRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    
    // Only fetch notifications if user is authenticated
    if (user) {
      // Fetch notifications initially
      fetchNotifications();
      
      // Poll for new notifications every 10 seconds
      const pollInterval = setInterval(fetchNotifications, 10000);
      
      return () => clearInterval(pollInterval);
    }
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getNotifications(50);
      console.log('Notifications response:', response.data);
      
      const notificationsList = response.data.notifications || [];
      setNotifications(notificationsList);
      
      // Count unread
      const unread = notificationsList.filter(n => !n.read).length;
      console.log('Unread count:', unread);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Don't reset notifications on error, keep previous state
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        await notificationsAPI.markAsRead(notif._id);
        // Update local state
        setNotifications(prev => 
          prev.map(n => n._id === notif._id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate based on notification type
    if (notif.type === 'like' || notif.type === 'comment') {
      navigate('/home');
    } else if (notif.type === 'follow') {
      navigate(`/profile/${notif.user.username}`);
    } else if (notif.type === 'trial') {
      navigate('/trials');
    } else if (notif.type === 'message') {
      navigate('/messages');
    } else {
      navigate('/home');
    }
  };

  // Check if we should show back button (not on home, login, or register pages)
  const showBackButton = !['/home', '/login', '/register', '/'].includes(location.pathname);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search for suggestions
  const fetchSuggestions = async (query) => {
    if (!query.trim()) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await searchAPI.search(query);
      const users = response.data.users || [];
      setSearchSuggestions(users.slice(0, 5)); // Show top 5 results
      setShowSuggestions(users.length > 0);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSearchSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300); // Wait 300ms after user stops typing
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
      setSearchQuery('');
    }
  };

  const handleSuggestionClick = (username) => {
    navigate(`/profile/${username}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-card/95 border-b border-border/50 shadow-sm backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Left: Back Button (conditional) + Profile Circle */}
            <div className="flex items-center space-x-2">
              {/* Back Button - Shows on all pages except home/login/register */}
              {showBackButton && (
                <button
                  onClick={handleBack}
                  className="p-2 rounded-full hover:bg-muted transition-all"
                  title="Go back"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
              )}

              {/* Profile Circle / Menu */}
              {currentUser ? (
              <button
                onClick={() => setDrawerOpen(true)}
                className="flex items-center space-x-2.5 hover:opacity-80 transition-opacity"
              >
                <Avatar className="w-9 h-9 cursor-pointer ring-2 ring-purple-100 hover:ring-purple-200 transition-all">
                  <AvatarImage src={currentUser?.profileImage} alt={currentUser?.name || 'User'} />
                  <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold text-sm">{currentUser?.name?.[0] || 'U'}</AvatarFallback>
                </Avatar>
                <span className="hidden md:block font-semibold text-sm text-foreground">Menu</span>
              </button>
              ) : (
              <div className="flex items-center space-x-2.5">
                <Skeleton className="w-9 h-9 rounded-full" />
                <Skeleton className="hidden md:block h-4 w-10" />
              </div>
              )}
            </div>

            {/* Middle: Search Bar with Suggestions */}
            <form onSubmit={handleSearch} className="flex-1 max-w-2xl mx-4 md:mx-8">
              <div className="relative" ref={searchRef}>
                <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <input
                  type="text"
                  placeholder="Search users or sports..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (searchQuery.trim() && searchSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  className="w-full pl-10 pr-10 py-2.5 rounded-full bg-muted/50 text-foreground text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all border-0"
                />
                
                {/* Clear button */}
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setShowSuggestions(false);
                      setSearchSuggestions([]);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}

                {/* Suggestions Dropdown */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border/50 rounded-2xl shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
                    {loadingSuggestions ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Loading...
                      </div>
                    ) : (
                      <div>
                        {searchSuggestions.map((user) => (
                          <button
                            key={user._id}
                            type="button"
                            onClick={() => handleSuggestionClick(user.username)}
                            className="w-full flex items-center space-x-3 p-3 hover:bg-muted/50 transition-colors cursor-pointer text-left"
                          >
                            <Avatar className="w-9 h-9 ring-2 ring-purple-100">
                              <AvatarImage src={user.profileImage} alt={user.name} />
                              <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold text-xs">{user.name?.[0] || 'U'}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-foreground truncate">{user.name}</p>
                              <div className="flex items-center space-x-2">
                                <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
                                {user.persona && (
                                  <Badge variant="secondary" className="text-xs capitalize px-2 py-0">
                                    {user.persona}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                        {searchQuery && (
                          <button
                            type="submit"
                            className="w-full p-3 text-center text-primary text-sm hover:bg-muted/50 transition-colors border-t border-border/50 font-medium"
                          >
                            View all results for "{searchQuery}"
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </form>

            {/* Right: Navigation Icons */}
            {/* Navigation Icons - Hidden on Mobile (using BottomNav instead) */}
            <div className="hidden md:flex items-center space-x-1">
              {/* Home Icon - Desktop Only */}
              <Link 
                to="/home" 
                className="p-2.5 rounded-full hover:bg-muted transition-all"
                title="Home"
              >
                <Home className="w-5 h-5 text-foreground" />
              </Link>

              {/* Trials Icon - Desktop Only */}
              <Link 
                to="/trials" 
                className="p-2.5 rounded-full hover:bg-muted transition-all"
                title="Trials"
              >
                <Trophy className="w-5 h-5 text-foreground" />
              </Link>

              {/* iCoach Icon - Desktop Only */}
              <Link 
                to="/ask-ai" 
                className="p-2.5 rounded-full hover:bg-muted transition-all"
                title="iCoach AI"
              >
                <Award className="w-5 h-5 text-foreground" />
              </Link>

              {/* AI Discover Icon - Desktop Only */}
              <Link 
                to="/ai-discover" 
                className="p-2 rounded-full hover:bg-secondary transition-all"
                title="AI Discover"
              >
                <Brain className="w-6 h-6 text-foreground" />
              </Link>

              {/* Notifications Icon - Now navigates to page */}
              <Link 
                to="/notifications" 
                className="relative p-2 rounded-full hover:bg-secondary transition-all"
                title="Notifications"
              >
                <Bell className="w-6 h-6 text-foreground" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs bg-primary text-white border-2 border-card">
                    {unreadCount}
                  </Badge>
                )}
              </Link>

              {/* Messages Icon */}
              <Link 
                to="/messages" 
                className="p-2 rounded-full hover:bg-secondary transition-all"
                title="Messages"
              >
                <MessageCircle className="w-6 h-6 text-foreground" />
              </Link>
            </div>

            {/* Mobile: Show messages and notifications */}
            <div className="flex md:hidden items-center space-x-0.5">
              {/* Notifications Icon - Now navigates to page */}
              <Link 
                to="/notifications" 
                className="relative p-1.5 rounded-full hover:bg-secondary transition-all"
              >
                <Bell className="w-5 h-5 text-foreground" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center p-0 text-[10px] bg-primary text-white border-2 border-card">
                    {unreadCount}
                  </Badge>
                )}
              </Link>

              {/* Messages Icon */}
              <Link 
                to="/messages" 
                className="relative outline-none p-1.5 hover:bg-secondary rounded-full transition-all"
              >
                <MessageCircle className="w-5 h-5 text-foreground" />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Profile Drawer */}
      <ProfileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
};

const getTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
};

export default Navbar;
